/**
 * `/about-us/news-events/`: the Ryerson News & Events landing page, with the latest news beside the
 * upcoming events. On any other `NEXT_PUBLIC_COMPANY` the page isn't built.
 *
 * Copy comes from `src/content/ryerson/about-us/news-events/index.ts`, whose `PageContent` is the
 * page's site-search entry (`scripts/staticSearch.ts`). `getStaticProps` fetches at build time the
 * header and footer data, the upcoming events
 * (`src/contentful/event/getContentfulUpcomingEvents.ts`) and the news list
 * (`src/contentful/news/getContentfulNewsForSections.ts`). Both lists are fixed at build time;
 * `components/NewsEvents/Events/UpcomingEventsSection.tsx` then drops, against the visitor's
 * clock, each event whose `date` has begun in UTC.
 *
 * Layout: from `lg` up, `LatestNewsSection` and `UpcomingEventsSection` sit in two half-width
 * columns; below `lg` they are the two tabs of a `components/Shared/AllSections/AllSections.tsx`
 * accordion. `src/pages/about-us/index.tsx` renders the same two sections in their `minimal` form.
 *
 * Each event card links to `/about-us/news-events/<slug>` (`./[eventSlug].tsx`); each news card
 * to `/about-us/news-events/news/<categorySlug>/<slug>` (`./news/[categorySlug]/[titleSlug].tsx`).
 * Pinned by `src/unit-test/AboutUs/NewsEvents.spec.tsx` (a render smoke test with empty lists).
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { Media } from "@ryerson/frontend.next";
import { ContentSection } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import LatestNewsSection from "@components/NewsEvents/News/LatestNewsSection";
import UpcomingEventsSection from "@components/NewsEvents/Events/UpcomingEventsSection";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import MetaHead from "@components/Shared/model/MetaHead";
import { useHasMounted } from "@components/Hooks";
import { StaticContent, MetaObject } from "@content/ryerson/about-us/news-events";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import getContentfulUpcomingEvents from "@contentful/event/getContentfulUpcomingEvents";
import { ProcessedContentfulUpcomingEventsByLanguage } from "@contentful/event/types";
import { getContentfulNewsForSections } from "@contentful/news/getContentfulNewsForSections";
import { ProcessedContentfulNewsForSectionsByLanguage } from "@contentful/news/types";

const Left = styled.div`
	display: inline-block;
	width: calc(50% - 20px);
	vertical-align: top;
	margin-right: 40px;
`;

const Right = styled.div`
	display: inline-block;
	width: calc(50% - 20px);
	vertical-align: top;
`;

/**
 * Ryerson build only. Fetches the header and footer data, the upcoming events and the news list
 * at build time; a Contentful failure fails the build.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			allUpcomingEventsByLanguage: await getContentfulUpcomingEvents(),
			contentfulNewsByLanguage: await getContentfulNewsForSections(),
		},
	};
};

/**
 * Renders the News & Events landing page in the current language; see the file header for the
 * layout.
 */
const Page: React.FC<
	HeaderPageProps & {
		allUpcomingEventsByLanguage: ProcessedContentfulUpcomingEventsByLanguage;
		contentfulNewsByLanguage: ProcessedContentfulNewsForSectionsByLanguage;
	}
> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	if (!useHasMounted()) return null;

	// The mobile accordion's tabs. Desktop renders the same two components directly below.
	const sections: Sections[] = [];
	sections.push({
		title: StaticContent[language].latestNewsLabel,
		cmp: (
			<LatestNewsSection
				content={StaticContent[language]}
				contentfulNewsByLanguage={props.contentfulNewsByLanguage}
			/>
		),
		maxHeight: "3000px",
	});
	sections.push({
		title: StaticContent[language].upcomingEventsLabel,
		cmp: (
			<UpcomingEventsSection
				content={StaticContent[language]}
				allUpcomingEventsByLanguage={props.allUpcomingEventsByLanguage}
			/>
		),
		maxHeight: "10000px",
	});
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<Media greaterThanOrEqual="lg">
				<ContentSection type="tertiary" vPadding="48px">
					<Typography
						variant="h1"
						type="tertiary"
						css={css`
							margin-bottom: 30px;
						`}
					>
						{StaticContent[language].title}
					</Typography>
					<Typography
						variant="div"
						size="md"
						type="tertiary"
						css={css`
							white-space: pre-line;
						`}
					>
						{StaticContent[language].description}
					</Typography>
				</ContentSection>
				<ContentSection type="secondary" vPadding="84px">
					<Left>
						<LatestNewsSection
							content={StaticContent[language]}
							contentfulNewsByLanguage={props.contentfulNewsByLanguage}
						/>
					</Left>
					<Right>
						<UpcomingEventsSection
							content={StaticContent[language]}
							allUpcomingEventsByLanguage={props.allUpcomingEventsByLanguage}
						/>
					</Right>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary" vPadding="28px">
					<Typography
						variant="h1"
						type="tertiary"
						css={css`
							margin-bottom: 24px;
						`}
					>
						{StaticContent[language].title}
					</Typography>
					<Typography variant="div" size="md" type="tertiary">
						{StaticContent[language].description}
					</Typography>
				</ContentSection>
				<AllSections sections={sections} />
			</Media>
		</HeaderLayout>
	);
};

export default Page;
